import { HelpCircle } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@monorepo-apps/ui/src/components/sheet";

import { PAGE_EXPLAINERS, type PageExplainerKey } from "@/presentation/data/page-explainers";

interface PageExplainerProps {
  pageKey: PageExplainerKey;
}

export function PageExplainer({ pageKey }: PageExplainerProps) {
  const explainer = PAGE_EXPLAINERS[pageKey];

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          className="group inline-flex items-center gap-1.5 self-end rounded-full border border-[oklch(0.72_0.17_190_/_0.25)] bg-[oklch(0.72_0.17_190_/_0.05)] px-2.5 py-1 text-[10.5px] font-medium text-[oklch(0.45_0.17_190)] transition-colors hover:border-[oklch(0.72_0.17_190_/_0.45)] hover:bg-[oklch(0.72_0.17_190_/_0.10)]"
        >
          <HelpCircle className="size-3.5" strokeWidth={2} />
          <span className="font-data tracking-[0.12em] uppercase">About this page</span>
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="flex w-full max-w-[380px] flex-col gap-0 overflow-y-auto p-0 sm:max-w-[380px]"
      >
        <SheetHeader className="border-border/60 border-b px-5 py-4">
          <span className="font-data text-[10px] tracking-[0.18em] text-[oklch(0.45_0.17_190)] uppercase">
            About this page
          </span>
          <SheetTitle className="text-base font-semibold tracking-tight">
            {explainer.title}
          </SheetTitle>
          <SheetDescription className="sr-only">
            Plain-language explanation of what this page is, how it&apos;s produced, and what it
            answers.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-6 px-5 py-5">
          <Section eyebrow="What this is">
            <p className="text-foreground/80 text-[13px] leading-relaxed">{explainer.whatThisIs}</p>
          </Section>

          <Section eyebrow="How we produce it">
            <ul className="flex flex-col gap-2">
              {explainer.howWeProduceIt.map((line, idx) => (
                <li key={idx} className="flex gap-2 text-[13px] leading-relaxed">
                  <span
                    className="mt-1.5 size-1.5 shrink-0 rounded-full bg-[oklch(0.55_0.17_190)]"
                    aria-hidden
                  />
                  <span className="text-foreground/80">{line}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section eyebrow="What it answers">
            <ul className="flex flex-col gap-2">
              {explainer.whatItAnswers.map((line, idx) => (
                <li key={idx} className="flex gap-2 text-[13px] leading-relaxed">
                  <span
                    className="mt-1.5 size-1 shrink-0 rotate-45 bg-[oklch(0.55_0.17_190)]"
                    aria-hidden
                  />
                  <span className="text-foreground/80">{line}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Section({ eyebrow, children }: { eyebrow: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="font-data text-foreground/55 text-[10px] font-semibold tracking-[0.18em] uppercase">
        {eyebrow}
      </span>
      {children}
    </div>
  );
}
